import { type PageConfig, type PageDefinition, titleFromId } from "./types";

/**
 * Every page in the app, found by folder: each `src/pages/<id>/page.ts` that
 * exports a `page`. There is no list to maintain — add (or copy) a folder and
 * the page is registered, delete it and it's gone.
 *
 * The id comes from the folder name in the glob key, not from the file, so a
 * copied folder is a new page with no edits.
 *
 * Only `page.ts` is loaded here, never a page's screen, API or queries, so the
 * app shell doesn't pull in a page's code just to draw a link to it.
 */
const modules = import.meta.glob<{ page: PageConfig }>("./*/page.ts", {
  eager: true,
});

/**
 * The template folder people copy to make a page. It's a real, compiled page
 * so it can't go stale, but it isn't a feature: it stays out of the sidebar
 * and landing page, and is reachable only by URL (`/example-page`) when
 * previewed. Copies have other folder names, so they're listed normally.
 */
export const TEMPLATE_PAGE_ID = "example-page";

const ALL_PAGES: readonly PageDefinition[] = Object.entries(modules)
  .map(([path, module]) => {
    const id = path.split("/")[1]; // "./hub-health/page.ts" → "hub-health"
    return {
      ...module.page,
      id,
      title: module.page.title ?? titleFromId(id),
    };
  })
  .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));

/** The pages the sidebar and landing page list: every page but the template. */
export const PAGES: readonly PageDefinition[] = ALL_PAGES.filter(
  (page) => page.id !== TEMPLATE_PAGE_ID,
);

/** Any page by folder name, the template included, for rendering its route. */
export function findPage(id: string): PageDefinition | undefined {
  return ALL_PAGES.find((page) => page.id === id);
}
